import { useState, useEffect } from 'react';
import { safeJsonParse } from '../utils/errorHandling';

// ============================================================================
// ACHIEVEMENT CELEBRATION SYSTEM - Red Team Customer Journey Fix
// Adds delight moments when users reach milestones
// ============================================================================
export const ACHIEVEMENTS = {
  first_scenario: { icon: '🎯', title: 'First Steps!', message: 'You created your first monitoring scenario. Examiners love documented scenarios.' },
  first_threshold: { icon: '📊', title: 'Threshold Master!', message: 'Your first threshold is documented. Now examiners can see your risk calibration.' },
  five_scenarios: { icon: '🏆', title: 'Getting Serious!', message: '5 scenarios documented. You\'re building a comprehensive program.' },
  perfect_eq: { icon: '⭐', title: 'Perfect 9/9!', message: 'Your scenario answers all Nine Questions. Examiner-ready!' },
  first_attestation: { icon: '✍️', title: 'Attested!', message: 'Your first attestation is recorded with cryptographic proof.' },
  exam_ready: { icon: '🎉', title: 'Exam Ready!', message: 'Congratulations! Your documentation is ready for regulatory examination.' },
};

function useAchievementTracker(scenarios = [], thresholds = [], attestations = [], setToast) {
  const [unlockedAchievements, setUnlockedAchievements] = useState(() => 
    safeJsonParse(localStorage.getItem('steadfastly_achievements'), [])
  );
  
  useEffect(() => {
    const newAchievements = [];
    
    // Check for first scenario
    if (scenarios.length >= 1 && !unlockedAchievements.includes('first_scenario')) {
      newAchievements.push('first_scenario');
    }
    
    // Check for first threshold
    if (thresholds.length >= 1 && !unlockedAchievements.includes('first_threshold')) {
      newAchievements.push('first_threshold');
    }
    
    // Check for 5 scenarios
    if (scenarios.length >= 5 && !unlockedAchievements.includes('five_scenarios')) {
      newAchievements.push('five_scenarios');
    }
    
    // Check for perfect EQ score
    if (scenarios.some(s => (s.eq_score || 0) >= 9) && !unlockedAchievements.includes('perfect_eq')) {
      newAchievements.push('perfect_eq');
    }
    
    // Check for first attestation
    if (attestations.length >= 1 && !unlockedAchievements.includes('first_attestation')) {
      newAchievements.push('first_attestation');
    }
    
    // Check for exam ready (5+ scenarios with 6+ EQ)
    if (scenarios.filter(s => (s.eq_score || 0) >= 6).length >= 5 && !unlockedAchievements.includes('exam_ready')) {
      newAchievements.push('exam_ready');
    }
    
    // Celebrate new achievements
    if (newAchievements.length > 0) {
      const allUnlocked = [...unlockedAchievements, ...newAchievements];
      setUnlockedAchievements(allUnlocked);
      localStorage.setItem('steadfastly_achievements', JSON.stringify(allUnlocked));
      
      // Show celebration toast for first new achievement
      const achievement = ACHIEVEMENTS[newAchievements[0]];
      if (achievement && setToast) {
        setToast({
          type: 'success',
          message: `${achievement.icon} ${achievement.title} - ${achievement.message}`,
          duration: 6000,
        });
      }
    }
  }, [scenarios.length, thresholds.length, attestations.length]);
  
  return { unlockedAchievements, totalAchievements: Object.keys(ACHIEVEMENTS).length };
}

export default useAchievementTracker;
